import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CatalogImage from '../catalog/CatalogImage';
import ProductPrice from '../catalog/ProductPrice';
import ProductRefId from '../catalog/ProductRefId';
import AddToCartButton from '../cart/AddToCartButton';
import ProductLetterRibbon from '../cart/ProductLetterRibbon';
import WishlistHeartButton from '../wishlist/WishlistHeartButton';
import { formatProductTitle } from '../formatProductTitle';
import { productDetailPath } from '../productPaths';
import { productEffectivePrice } from '../productPricing';
import {
  hrefForSameSiteAsset,
  productGalleryImagePaths,
  productHasVideo,
  primaryVideoSource,
  videoPosterSrc,
} from '../productMediaUtils';
import { productCharPersonalizationEnabled } from '../productPersonalization';
import { productJewelryCustomizationEnabled } from '../jewelry/jewelryCustomization';
import ChevronIcon from './icons/ChevronIcon';

/**
 * Shared storefront product card — grid listings, featured strip, gift finder.
 */
export default function ProductCard({
  product,
  categorySlug,
  variant = 'grid',
  className = '',
  showRefId = true,
  compact = true,
  linkToDetail = true,
  onMediaClick,
  onMediaDoubleClick,
  onBeforeNavigate,
  imageIndex: controlledIndex,
  onImageIndexChange,
  imageSizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
  subtitle,
}) {
  const navigate = useNavigate();
  const cat = categorySlug || product?.category || '';
  const [internalIndex, setInternalIndex] = useState(0);
  const imageIndex = controlledIndex ?? internalIndex;

  const galleryPaths = productGalleryImagePaths(product);
  const hasMultipleImages = galleryPaths.length > 1;
  const isVideo = productHasVideo(product);
  const videoSrc = primaryVideoSource(product);
  const currentImage = galleryPaths[Math.min(Math.max(0, imageIndex), galleryPaths.length - 1)] || galleryPaths[0] || '';
  const detailPath = productDetailPath({ ...product, category: cat });
  const title = formatProductTitle(product?.name || '');
  const isFeatured = variant === 'featured';
  const isCustomizable =
    productCharPersonalizationEnabled(product) ||
    productJewelryCustomizationEnabled(product) ||
    product?.type === 'personalized';

  const goImage = useCallback(
    (direction, e) => {
      e?.stopPropagation?.();
      e?.preventDefault?.();
      if (!hasMultipleImages) return;
      const idx = imageIndex;
      const newIndex =
        direction === 'next'
          ? (idx + 1) % galleryPaths.length
          : idx === 0
            ? galleryPaths.length - 1
            : idx - 1;
      if (onImageIndexChange) onImageIndexChange(newIndex);
      else setInternalIndex(newIndex);
    },
    [galleryPaths.length, hasMultipleImages, imageIndex, onImageIndexChange]
  );

  const handleMediaClick = (e) => {
    if (
      e?.target?.closest?.(
        '.product-card__gallery-nav, .product-card__wishlist, .add-to-cart-block, .wishlist-heart-btn'
      )
    ) {
      return;
    }
    if (onMediaClick) {
      onMediaClick(product, e);
      return;
    }
    if (linkToDetail && detailPath && detailPath !== '/shop') {
      onBeforeNavigate?.(product);
      navigate(detailPath);
    }
  };

  const handleMediaKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') handleMediaClick(e);
  };

  const mediaInner = isVideo && videoSrc ? (
    <div className="product-card__video-thumb">
      <img src={videoPosterSrc(product)} alt={title} loading="lazy" />
      <span className="product-card__play" aria-hidden>
        ▶
      </span>
    </div>
  ) : (
    <>
      <CatalogImage
        src={currentImage}
        alt={title}
        className="product-card__img"
        loading="lazy"
        sizes={isFeatured ? '220px' : imageSizes}
      />
      {hasMultipleImages ? (
        <div className="product-card__gallery-nav" onClick={(e) => e.stopPropagation()} role="presentation">
          <button type="button" className="product-card__nav-btn" onClick={(e) => goImage('prev', e)} aria-label="Previous image">
            <ChevronIcon direction="left" />
          </button>
          <span className="product-card__counter" aria-live="polite">
            {imageIndex + 1}/{galleryPaths.length}
          </span>
          <button type="button" className="product-card__nav-btn" onClick={(e) => goImage('next', e)} aria-label="Next image">
            <ChevronIcon direction="right" />
          </button>
        </div>
      ) : null}
    </>
  );

  const mediaShell = isFeatured ? (
    <div className="product-image-wrapper product-card__media">
      <ProductLetterRibbon product={product} />
      <WishlistHeartButton product={product} categorySlug={cat} size="sm" className="product-card__wishlist" />
      {isCustomizable ? (
        <span className="product-card__custom-badge">Customizable</span>
      ) : null}
      <Link to={detailPath} className="product-card__media-link" tabIndex={-1} aria-hidden>
        {mediaInner}
      </Link>
    </div>
  ) : (
    <div
      className={`product-media-container product-card__media${linkToDetail && !onMediaClick ? ' product-media-container--link' : ''}`}
      onClick={handleMediaClick}
      onDoubleClick={onMediaDoubleClick ? (e) => onMediaDoubleClick(product, e) : undefined}
      onKeyDown={linkToDetail || onMediaClick ? handleMediaKeyDown : undefined}
      role={linkToDetail || onMediaClick ? 'link' : undefined}
      tabIndex={linkToDetail || onMediaClick ? 0 : undefined}
      aria-label={linkToDetail || onMediaClick ? `View ${title}` : undefined}
    >
      <ProductLetterRibbon product={product} />
      <WishlistHeartButton product={product} categorySlug={cat} size="sm" className="product-card__wishlist" />
      {mediaInner}
      {productCharPersonalizationEnabled(product) || product.type === 'personalized' ? (
        <span className="personalized-badge">Personalized</span>
      ) : null}
    </div>
  );

  const titleNode = (
    <h3 className={isFeatured ? 'product-title' : 'product-title'}>
      <Link to={detailPath} className="text-decoration-none text-body">
        {title}
      </Link>
    </h3>
  );

  const priceNode = <ProductPrice product={product} className="product-price" showBadge={false} />;

  const purchaseNode =
    productEffectivePrice(product) > 0 ? (
      <AddToCartButton
        product={product}
        categorySlug={cat}
        compact={compact}
        variant={isFeatured ? 'featured' : undefined}
        className={isFeatured ? undefined : 'mt-2'}
      />
    ) : (
      <p className="small text-muted mb-0 mt-2 text-center">Price on request</p>
    );

  return (
    <article className={`product-card product-card--v2${isFeatured ? ' featured' : ''} ${className}`.trim()}>
      {mediaShell}
      {isFeatured ? (
        <div className="product-card__summary">
          {titleNode}
          {priceNode}
        </div>
      ) : (
        <div className="product-info">
          {titleNode}
          {subtitle ? <p className="product-card__subtitle small text-muted mb-1">{subtitle}</p> : null}
          {showRefId ? <ProductRefId id={product.id} /> : null}
          {priceNode}
          {purchaseNode}
        </div>
      )}
    </article>
  );
}

/** Resolve href for hero / OG fallbacks from catalog paths. */
export function catalogAssetHref(path) {
  const p = String(path || '').trim();
  if (!p) return '';
  return hrefForSameSiteAsset(p);
}
